import type { ReactNode } from 'react';
import type { RidesCampusView, RidesEventView, RidesPublicSettingsView } from '@actschurch/ccms-sdk';
import { campusById, serviceTimeById, serviceTimesFor, useRidesEvents, useRidesLookups } from '@/lib/lookups';
import { formatShortDate, formatTime } from '@/lib/format';
import { useRidesSettings } from '@/lib/settings';
import { useRidesWizard } from '@/store/ridesWizard';
import { BackLink } from '@/components/rides/PageIntro';
import '@/styles/flows.css';

/** Kept for older imports: the offer-a-lift flow now has 5 or 6 screens (see offerPlan). */
export const DRIVER_STEPS = 5;

// ---------------------------------------------------------------- offer a lift (repeatable)

export type OfferStep = 'events' | 'from' | 'to' | 'service' | 'details' | 'review';

export function offerPlan(needsService: boolean): OfferStep[] {
  return needsService ? ['events', 'from', 'to', 'service', 'details', 'review'] : ['events', 'from', 'to', 'details', 'review'];
}

/** A chosen event uses campus service times and the drop-off campus has some (unknown campus = assume yes). */
export function driverNeedsService(chosen: RidesEventView[], drop: RidesCampusView | undefined): boolean {
  if (!chosen.some((e) => e.usesCampusServiceTimes)) return false;
  return drop ? serviceTimesFor(drop).length > 0 : true;
}

/** "HH:mm" minus `minutes` (wraps past midnight). */
export function minusMinutes(hhmm: string, minutes: number): string {
  const t = formatTime(hhmm);
  const [h, m] = t.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return t;
  const total = (((h * 60 + m - minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * The departure time riders will see when the driver leaves the time blank: the API uses
 * settings.defaultDepartureLeadMinutes before the service (or the event's own start time).
 * null when it depends on the event (several different times) or the start time isn't known.
 */
export function defaultDeparture(chosen: RidesEventView[], serviceStart: string | null | undefined, settings: RidesPublicSettingsView): string | null {
  const lead = Math.max(0, settings.defaultDepartureLeadMinutes || 0);
  if (serviceStart) return minusMinutes(serviceStart, lead);
  const times = Array.from(new Set(chosen.filter((e) => !e.usesCampusServiceTimes).map((e) => e.defaultStartTime ?? '')));
  if (times.length === 1 && times[0] && !chosen.some((e) => e.usesCampusServiceTimes)) return minusMinutes(times[0], lead);
  return null;
}

/** Everything the offer-a-lift screens share. */
export function useOfferFlow() {
  const { driver, setDriver, resetDriver } = useRidesWizard();
  const lookups = useRidesLookups();
  const events = useRidesEvents();
  const { settings } = useRidesSettings();
  const chosen = (events.data ?? []).filter((e) => driver.eventIds.includes(e.eventID)).sort((a, b) => a.eventDate - b.eventDate);
  const col = campusById(lookups.data, driver.collectionLocationId);
  const drop = campusById(lookups.data, driver.dropOffLocationId);
  const svc = serviceTimeById(drop, driver.serviceTimeId);
  const needsService = driverNeedsService(chosen, drop);
  const timeRequired = needsService && settings.requireServiceTimeForSundays;
  const plan = offerPlan(needsService);
  const stepOf = (k: OfferStep) => Math.max(1, plan.indexOf(k) + 1);

  const chips = (upTo: OfferStep): string[] => {
    const order: OfferStep[] = ['events', 'from', 'to', 'service', 'details'];
    const before = (k: OfferStep) => order.indexOf(k) < order.indexOf(upTo) || upTo === 'review';
    const out: string[] = [];
    const st = before('service') && needsService && svc ? `, ${formatTime(svc.startTime)}` : '';
    if (before('events') && chosen.length) out.push(chosen.length === 1 ? `${chosen[0].name}, ${formatShortDate(chosen[0].date)}${st}` : `${chosen.length} services${st}`);
    if (before('to') && col && drop) out.push(`${col.name} to ${drop.name}`);
    else if (before('from') && col) out.push(`From ${col.name}`);
    return out;
  };

  return { driver, setDriver, resetDriver, lookups, events, settings, chosen, col, drop, svc, needsService, timeRequired, plan, total: plan.length, stepOf, chips };
}

// ---------------------------------------------------------------- one-time driver setup

export type SetupPart = 'you' | 'car' | 'licence' | 'contact' | 'promise' | 'id' | 'licence-check' | 'docs';

/** The first-time setup, in order: the profile screens, then the checks that still apply. */
export function setupPlan(o: { contactAsked: boolean; idNeeded: boolean; licenceFix: boolean; docsAsked: boolean }): SetupPart[] {
  const plan: SetupPart[] = ['you', 'car', 'licence'];
  if (o.contactAsked) plan.push('contact');
  plan.push('promise');
  if (o.idNeeded) plan.push('id');
  if (o.licenceFix) plan.push('licence-check');
  if (o.docsAsked) plan.push('docs');
  return plan;
}

/** Header for editing one item from the checklist (not part of a numbered flow). */
export function EditHeader({ title, lede, backTo = '/drive/profile', backLabel = 'Back to your checklist' }: { title: ReactNode; lede?: ReactNode; backTo?: string; backLabel?: string }) {
  return (
    <header className="rides-stephead rides-edithead">
      <BackLink to={backTo} label={backLabel} />
      <h1 className="rides-title">{title}</h1>
      {lede && <p className="rides-lede">{lede}</p>}
    </header>
  );
}
